import {
    deleteConditionalRebatesFailure,
    deleteConditionalRebatesRequest,
    deleteConditionalRebatesSuccess,
    getConditionalRebatesFailure,
    getConditionalRebatesRequest,
    getConditionalRebatesSuccess,
    patchConditionalRebatesHandRaisersFailure,
    patchConditionalRebatesHandRaisersRequest,
    patchConditionalRebatesHandRaisersSuccess,
    putConditionalRebatesFailure,
    putConditionalRebatesRequest,
    putConditionalRebatesSuccess,
} from "src/store/actions/conditionalRebates";
import { getDealPersonSuccess, getDealSuccess } from "src/store/actions/deal";
import { pollingSuccess } from "src/store/actions/poller";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { requestFinished, requestStarted } from "src/utils/conditionalRebates";
import { ConditionalRebateState } from "Types/state";
import reject from "lodash-es/reject";

export enum ConditionalRebatesStatus {
    Idle = "idle",
    Loading = "loading",
}

type AnyStatusActions =
    | ActionType<typeof getConditionalRebatesRequest>
    | ActionType<typeof putConditionalRebatesRequest>
    | ActionType<typeof deleteConditionalRebatesRequest>
    | ActionType<typeof patchConditionalRebatesHandRaisersRequest>
    | ActionType<typeof getDealPersonSuccess>;

type LoadingStatusActions =
    | AnyStatusActions
    | ActionType<typeof getConditionalRebatesSuccess>
    | ActionType<typeof getConditionalRebatesFailure>
    | ActionType<typeof putConditionalRebatesSuccess>
    | ActionType<typeof putConditionalRebatesFailure>
    | ActionType<typeof deleteConditionalRebatesSuccess>
    | ActionType<typeof deleteConditionalRebatesFailure>
    | ActionType<typeof patchConditionalRebatesHandRaisersSuccess>
    | ActionType<typeof patchConditionalRebatesHandRaisersFailure>;

type IdleStatusActions = AnyStatusActions | ActionType<typeof getDealSuccess> | ActionType<typeof pollingSuccess>;

const anyStatusHandler = (state: ConditionalRebateState, action: AnyStatusActions) => {
    switch (action.type) {
        case getConditionalRebatesRequest.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    available: {
                        ...state.dataDriven.available,
                        [action.payload.vin]: null,
                    },
                },
                ...requestStarted(state),
            };
        case deleteConditionalRebatesRequest.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    selected: reject(state.dataDriven.selected, action.payload.conditionalRebate),
                },
                ...requestStarted(state),
            };
        case putConditionalRebatesRequest.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    selected: [...state.dataDriven.selected, action.payload.conditionalRebate],
                },
                ...requestStarted(state),
            };
        case patchConditionalRebatesHandRaisersRequest.type:
            return {
                ...state,
                handRaisers: {
                    ...state.handRaisers,
                    [action.payload.id]: action.payload.value,
                },
                ...requestStarted(state),
            };
        case getDealPersonSuccess.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    selected: action.payload.person?.conditional_rebates ?? state.dataDriven.selected,
                },
            };
        default:
            return state;
    }
};

const loadingStatusHandler = (state: ConditionalRebateState, action: LoadingStatusActions) => {
    switch (action.type) {
        case getConditionalRebatesSuccess.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    available: action.payload.conditionalRebates,
                },
                ...requestFinished(state),
            };
        case putConditionalRebatesFailure.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    selected: reject(state.dataDriven.selected, action.payload.conditionalRebate),
                },
                ...requestFinished(state),
            };
        case deleteConditionalRebatesFailure.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    selected: [...state.dataDriven.selected, action.payload.conditionalRebate],
                },
                ...requestFinished(state),
            };
        case patchConditionalRebatesHandRaisersFailure.type:
            return {
                ...state,
                handRaisers: {
                    ...state.handRaisers,
                    [action.payload.id]: action.payload.value,
                },
                ...requestFinished(state),
            };
        case getConditionalRebatesFailure.type:
        case putConditionalRebatesSuccess.type:
        case deleteConditionalRebatesSuccess.type:
        case patchConditionalRebatesHandRaisersSuccess.type:
            return {
                ...state,
                ...requestFinished(state),
            };
        default:
            return anyStatusHandler(state, action);
    }
};

const idleStatusHandler = (state: ConditionalRebateState, action: IdleStatusActions) => {
    switch (action.type) {
        case getDealSuccess.type:
        case pollingSuccess.type:
            return {
                ...state,
                dataDriven: {
                    ...state.dataDriven,
                    selected: action.payload.deal.person?.conditional_rebates ?? state.dataDriven.selected,
                },
                handRaisers: {
                    ...state.handRaisers,
                    ...action.payload.deal.rebates,
                },
            };
        default:
            return anyStatusHandler(state, action);
    }
};

const conditionalRebatesReducer = (
    state: ConditionalRebateState = {
        dataDriven: {
            available: {},
            selected: [],
        },
        handRaisers: {
            college: false,
            conquest: false,
            loyalty: false,
            military: false,
        },
        status: ConditionalRebatesStatus.Idle,
        requests: 0,
    },
    action: IdleStatusActions | LoadingStatusActions
) => {
    if (state.status === ConditionalRebatesStatus.Loading) {
        return loadingStatusHandler(state, action as LoadingStatusActions);
    }

    return idleStatusHandler(state, action as IdleStatusActions);
};

export default conditionalRebatesReducer;
